import { useOrg } from '@/contexts/OrgContext';
import { useEffect, useRef, useSyncExternalStore } from 'react';

export interface RecentItem {
  id: string | number;
  label: string;
  visitedAt: number;
}

function scopedKey(baseKey: string, slug: string | null | undefined): string {
  return slug ? `${baseKey}:${slug}` : baseKey;
}

const MAX_ITEMS = 5;
const EMPTY: RecentItem[] = [];

const cache = new Map<string, { raw: string; parsed: RecentItem[] }>();

function getSnapshot(key: string): RecentItem[] {
  const raw = localStorage.getItem(key) ?? '';
  const cached = cache.get(key);

  if (cached && cached.raw === raw) return cached.parsed;

  if (!raw) {
    cache.set(key, { raw: '', parsed: EMPTY });
    return EMPTY;
  }
  try {
    const parsed = JSON.parse(raw) as RecentItem[];

    cache.set(key, { raw, parsed });
    return parsed;
  } catch {
    cache.set(key, { raw: '', parsed: EMPTY });
    return EMPTY;
  }
}

function writeItems(key: string, items: RecentItem[]) {
  try {
    const raw = JSON.stringify(items);

    localStorage.setItem(key, raw);
    cache.set(key, { raw, parsed: items });
  } catch {
    // ignore
  }
}

const listeners = new Map<string, Set<() => void>>();

function subscribe(key: string, cb: () => void) {
  let set = listeners.get(key);

  if (!set) {
    set = new Set();
    listeners.set(key, set);
  }
  set.add(cb);
  return () => {
    set!.delete(cb);
  };
}

function notify(key: string) {
  listeners.get(key)?.forEach((cb) => cb());
}

export function useRecentItems(baseKey: string): RecentItem[] {
  const { currentOrg } = useOrg();
  const key = scopedKey(baseKey, currentOrg?.tenantSlug);

  return useSyncExternalStore(
    (cb) => subscribe(key, cb),
    () => getSnapshot(key),
  );
}

export function useTrackRecentItem(baseKey: string, item: { id: string | number; label: string } | null) {
  const { currentOrg } = useOrg();
  const slug = currentOrg?.tenantSlug;
  const key = scopedKey(baseKey, slug);
  const trackedSlug = useRef(slug);

  useEffect(() => {
    if (!item) return;

    // prevent stale tracking when switching orgs
    if (trackedSlug.current !== slug) {
      trackedSlug.current = slug;
      return;
    }

    const existing = getSnapshot(key);
    const filtered = existing.filter((r) => r.id !== item.id);
    const updated = [{ id: item.id, label: item.label, visitedAt: Date.now() }, ...filtered].slice(0, MAX_ITEMS);

    writeItems(key, updated);
    notify(key);
  }, [key, slug, item?.id, item?.label]);
}
